import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import dayjs from 'dayjs';
import {
  ControlledDatePicker,
  ControlledImageUpload,
  ControlledSelect,
  ControlledTextField,
  SectionTitle,
} from 'modules/shared/components';
import { calculateAge } from 'modules/shared/utils';
import { AGE_LIMITS, GENDER_OPTIONS, PERSONAL_DETAILS_LIMITS } from '../constants';

const FIELD_PROPS = {
  orientation: 'row',
  wrapperSx: {
    '& > div': {
      columnGap: 4,
      '& > :nth-child(2n)': { justifySelf: 'flex-end', width: 360 },
    },
  },
  sx: { width: '100%' },
};

export default function PersonalDetailsSection() {
  const { watch, setValue } = useFormContext();
  const photoUploadRef = useRef(null);

  const photo = watch('personalData.photo');
  const dob = watch('personalData.dateOfBirth');

  useEffect(() => {
    if (!dob) {
      setValue('personalData.age', '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      return;
    }

    setValue('personalData.age', calculateAge(dob) ?? '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dob, setValue]);

  const today = dayjs();
  const MAX_DOB = today.subtract(AGE_LIMITS.MIN, 'year');
  const MIN_DOB = today.subtract(AGE_LIMITS.MAX, 'year');

  return (
    <div className='column gap-3'>
      <SectionTitle title='Personal details' variant='subtitle' />

      <div className='grid grid--2-cols grid--with-divider'>
        <div className='column'>
          <PhotoUploadBlock photo={photo} uploadRef={photoUploadRef} />
          <ControlledTextField
            name='personalData.name'
            label='Full name'
            placeholder='Enter full name'
            rules={{
              required: 'Full name is required',
              maxLength: {
                value: PERSONAL_DETAILS_LIMITS.NAME_MAX,
                message: `Maximum ${PERSONAL_DETAILS_LIMITS.NAME_MAX} characters allowed`,
              },
            }}
            {...FIELD_PROPS}
          />
          <ControlledDatePicker
            name='personalData.dateOfBirth'
            label='Date of birth'
            minDate={MIN_DOB}
            maxDate={MAX_DOB}
            disableFuture
            rules={{
              required: 'Date of birth is required',
              validate: (value) => {
                if (!value) return true;

                const dob = dayjs(value);
                if (!dob.isValid()) return 'Date of birth is invalid';

                if (dob.isAfter(MAX_DOB)) {
                  return `Candidate must be at least ${AGE_LIMITS.MIN} years old`;
                }

                if (dob.isBefore(MIN_DOB)) {
                  return `Candidate must be at most ${AGE_LIMITS.MAX} years old`;
                }

                return true;
              },
            }}
            {...FIELD_PROPS}
          />
          <ControlledTextField
            name='personalData.age'
            label='Age'
            type='number'
            disabled
            {...FIELD_PROPS}
          />

          <ControlledSelect
            name='personalData.gender'
            label='Gender'
            options={GENDER_OPTIONS}
            rules={{ required: 'Gender is required' }}
            {...FIELD_PROPS}
          />
        </div>
        <div className='column'>
          <ControlledTextField
            name='personalData.contact'
            label='Contact number'
            type='tel'
            inputMode='numeric'
            rules={{
              required: 'Contact number is required',
              minLength: {
                value: PERSONAL_DETAILS_LIMITS.PHONE_LENGTH,
                message: 'Contact number must be 10 digits',
              },
              maxLength: {
                value: PERSONAL_DETAILS_LIMITS.PHONE_LENGTH,
                message: 'Contact number must be 10 digits',
              },
            }}
            {...FIELD_PROPS}
          />

          <ControlledTextField
            name='personalData.email'
            label='Email ID'
            type='email'
            rules={{ required: 'Email ID is required' }}
            {...FIELD_PROPS}
          />

          <ControlledTextField
            name='personalData.residentialAddress'
            label='Residential address'
            rules={{ required: 'Residential address is required' }}
            {...FIELD_PROPS}
          />

          <ControlledTextField
            name='personalData.permanentAddress'
            label='Permanent address'
            rules={{ required: 'Permanent address is required' }}
            {...FIELD_PROPS}
          />

          <ControlledTextField
            name='personalData.nationality'
            label='Nationality'
            rules={{ required: 'Nationality is required' }}
            {...FIELD_PROPS}
          />

          <ControlledTextField
            name='personalData.medicalCondition'
            label='Medical condition (if any)'
            {...FIELD_PROPS}
          />
        </div>
      </div>
    </div>
  );
}

function PhotoUploadBlock({ photo, uploadRef }) {
  return (
    <div className='row gap-4 row--between'>
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: 1,
          position: 'relative',
          border: '1px solid',
          borderColor: 'grey.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
          color: (theme) => alpha(theme.palette.grey[500], 0.5),
          '& button': { position: 'absolute', bottom: 4, right: 4 },
        }}
      >
        {photo?.url ? (
          <img src={photo.url} alt='' />
        ) : (
          <Icon icon='fluent:person-16-filled' width={80} />
        )}
        <button
          type='button'
          className='btn text-btn'
          onClick={() => uploadRef.current?.openUploader()}
        >
          <Icon icon='line-md:edit-twotone' />
        </button>
      </Box>

      <ControlledImageUpload
        ref={uploadRef}
        name='personalData.photo'
        showPreview={false}
        buttonText='Upload photo'
        aspectRatio='1:1'
      />
    </div>
  );
}
